import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addIndirectUseSoldToProduct,
  getProductIndirectUseSoldList,
  updateProductIndirectUseSold,
  deleteProductIndirectUseSold,
} from "../../../../../../apis/Product/IndirectUseSoldApi";
import {
  getProdIndirectEmitDropdown,
  getCompIndirectEnergyDropdown,
  getTransportCountryData,
  getCompUseGwpDropdown,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const IndirectUseSoldTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [dropdownGWP, setDropdownGWP] = useState([]);
  const [dropdownEnergy, setDropdownEnergy] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProductIndirectUseSoldList({
      productId,
      setObjects: setData,
    });
    getProdIndirectEmitDropdown({ setDropdown: setDropdownProcess });
    getTransportCountryData({ setDropdownData: setDropdownCountry });
    getCompUseGwpDropdown({ setDropdown: setDropdownGWP });
    getCompIndirectEnergyDropdown({ setDropdown: setDropdownEnergy });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownProcess?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["process_emitted"]?.value || row.process_emitted;

        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) =>
              option?.dropdown === materialValue ||
              option?.dropdown_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      process_emitted: {
        targetField: "amount_per_use",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown === selectedValue ||
              item.dropdown_en === selectedValue
          );
          if (selectedItem) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedItem.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedItem.unit,
              }));
            }
          }
        },
        dropdownDataKey: "process_emitted",
      },
    }),
    []
  );

  const getFieldValue = useCallback((field) => {
    if (!field) return null;
    return typeof field === "object" ? field.value : field;
  }, []);

  const handleEdit = useCallback(
    (editedItem) => {
      updateProductIndirectUseSold({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        process_emitted: getFieldValue(editedItem.process_emitted),
        country_sold: getFieldValue(editedItem.country_sold),
        greenhouse: getFieldValue(editedItem.greenhouse),
        energy_added: getFieldValue(editedItem.energy_added),
        amount_per_use: editedItem["customMulti_amount_per_use"]
          ? editedItem["customMulti_amount_per_use"]
          : editedItem["amount_per_use"],
        amount_per_lifetime: editedItem["customMulti_amount_per_lifetime"]
          ? editedItem["customMulti_amount_per_lifetime"]
          : editedItem["amount_per_lifetime"],
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, setData, getFieldValue]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addIndirectUseSoldToProduct({
        productId,
        ...newItem,
        process_emitted: getFieldValue(newItem.process_emitted),
        country_sold: getFieldValue(newItem.country_sold),
        greenhouse: getFieldValue(newItem.greenhouse),
        energy_added: getFieldValue(newItem.energy_added),
        amount_per_use: newItem["customMulti_amount_per_use"],
        amount_per_lifetime: newItem["customMulti_amount_per_lifetime"],
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, getFieldValue]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductIndirectUseSold({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "country_sold",
        header: `${t("COMP_DIRECT_COUNTRY")}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("INDIRECT_COUNTRY_TIP"),
      },
      {
        accessor: "percentage_sold",
        header: `${t("COMP_INDIRECT_PERC")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },

      {
        accessor: "process_emitted",
        header: `${t("COMP_PROCESS_EMIT")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "150px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_EMIT_TIP"),
      },
      {
        accessor: "energy_added",
        header: `${t("COMP_INDIRECT_ENERGY_ADDED")}`,
        type: "dropdown",
        dropdownData: dropdownEnergy,
        disableSortBy: true,
        width: "150px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_ENERGY_ADDED_USE_TIP"),
      },
      {
        accessor: "energy_added_times",
        header: `${t("COMP_INDIRECT_ENERGY_TIMES")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        isNABtn: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_ENERGY_LIFETIME_TIP"),
      },

      {
        accessor: "customMulti",
        header: `${t("COMP_EXPECTED_AMOUNT")}`,
        type: "multipleCustom",
        wide: true,
        layout: "horizontal",
        optional: true,
        width: "300px",
        customInputs: [
          {
            accessor: "amount_per_use",
            header: `${t("per use")}`,
            type: "number",
            disableSortBy: true,
            optional: true,
            placeholder: "0",
            width: "60px",
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per use")}`,
          },
          {
            accessor: "amount_per_lifetime",
            header: `${t("per lifetime")}`,
            type: "number",
            optional: true,
            disableSortBy: true,
            width: "60px",
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per life")}`,
          },
        ],
      },
      {
        accessor: "greenhouse",
        header: `${t("COMP_GREENHOUSE")}`,
        type: "dropdown",
        dropdownData: dropdownGWP,
        disableSortBy: true,
        width: "130px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_GREENHOUSE_TIP"),
      },
      {
        accessor: "gas_charged",
        header: `${t("COMP_GWP_NUM")}`,
        type: "number",
        optional: true,
        disableSortBy: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_GAS_CHARGED_TIP"),
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [
      dropdownProcess,
      dropdownCountry,
      unitMaterials,
      dropdownGWP,
      dropdownEnergy,
      t,
    ]
  );

  const disableRules = {
    carbon: () => true,
  };

  const noShow = {
    energy_added: (rowData) => {
      const processValue =
        typeof rowData.process_emitted === "object"
          ? rowData.process_emitted?.value
          : rowData.process_emitted;
      return processValue !== "Process missing";
    },
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      process_emitted: dropdownProcess,
      country_sold: dropdownCountry,
      greenhouse: dropdownGWP,
      energy_added: dropdownEnergy,
    }),
    [dropdownProcess, dropdownCountry, dropdownGWP, dropdownEnergy]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
      noShow={noShow}
    />
  );
};

export default IndirectUseSoldTable;
